import { makeAutoObservable, observable, runInAction } from "mobx";
import type { RootStore } from "./root.store";

export class UserSkillsStore {
  assignedSkillIdsBySlug = observable.map<string, Set<string>>();
  isLoading = false;
  isSaving = false;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  getAssignedSkillIds(slug: string): Set<string> {
    return this.assignedSkillIdsBySlug.get(slug) ?? new Set<string>();
  }

  get assignedSkillIds(): Set<string> {
    const slug = this.rootStore.router.workspaceSlug;
    if (!slug) return new Set<string>();
    return this.getAssignedSkillIds(slug);
  }

  hasSkill(slug: string, skillId: string): boolean {
    return this.assignedSkillIdsBySlug.get(slug)?.has(skillId) ?? false;
  }

  async fetchMySkills(slug: string): Promise<string[]> {
    this.isLoading = true;
    try {
      const assignments = await this.rootStore.workspaceSkillService.listMySkills(slug);
      const skillIds = assignments.map((entry) => entry.skill).filter(Boolean);
      runInAction(() => {
        this.assignedSkillIdsBySlug.set(slug, new Set(skillIds));
      });
      return skillIds;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async saveMySkills(slug: string, skillIds: string[]): Promise<void> {
    this.isSaving = true;
    try {
      await this.rootStore.workspaceSkillService.setMySkills(slug, skillIds);
      runInAction(() => {
        this.assignedSkillIdsBySlug.set(slug, new Set(skillIds));
      });
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }
}
